<template>
  <div class="position-relative">
    <div class="d-flex justify-content-between align-items-start flex-wrap mb-1" style="gap: 1rem;">
      <div>
        <h2 class="mb-25">CRM - Clientes</h2>
        <p class="text-muted mb-0">Gerencie sua carteira de clientes</p>
      </div>
      <div class="d-flex align-items-center" style="gap: 0.5rem;">
        <b-badge
          v-if="qualityData"
          :variant="'light-' + gradeVariant"
          class="d-inline-flex align-items-center cursor-pointer"
          style="gap: 0.4rem; padding: 0.4rem 0.7rem; font-size: 0.85rem;"
          @click="toggleQualityPanel"
          @mouseenter="startHoverTimer"
          @mouseleave="cancelHoverTimer"
        >
          <b-badge :variant="gradeVariant" pill>{{ qualityData.grade }}</b-badge>
          <span>{{ qualityData.score }}</span>
          <feather-icon icon="ChevronDownIcon" size="12" />
        </b-badge>
        <b-spinner v-else-if="qualityLoading" small variant="secondary" />
        <b-button variant="outline-secondary" size="sm" @click="loadClientes">
          <feather-icon icon="RefreshCwIcon" size="14" />
        </b-button>
      </div>
    </div>

    <transition name="quality-slide">
      <b-card
        v-if="showQualityPanel && qualityData"
        class="quality-panel shadow"
        no-body
        @mouseenter="cancelHoverTimer"
        @mouseleave="closeQualityPanel"
      >
        <div class="p-1">
          <div class="d-flex align-items-start justify-content-between pb-1 border-bottom">
            <div class="d-flex align-items-center" style="gap: 0.875rem;">
              <div class="position-relative" style="width: 64px; height: 64px;">
                <svg viewBox="0 0 80 80" class="w-100 h-100" style="transform: rotate(-90deg);" :class="'text-' + gradeVariant">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" stroke-opacity="0.15" stroke-width="6" />
                  <circle
                    cx="40" cy="40" r="34"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="6"
                    stroke-linecap="round"
                    :stroke-dasharray="circumference"
                    :stroke-dashoffset="scoreOffset"
                    style="transition: stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1);"
                  />
                </svg>
                <div class="position-absolute" style="top: 50%; left: 50%; transform: translate(-50%, -50%);">
                  <strong style="font-size: 1.25rem;">{{ animatedScore }}</strong>
                </div>
              </div>
              <div>
                <div class="font-weight-bold">Qualidade da Carteira</div>
                <div class="text-muted small">
                  {{ formatNum(qualityData.total_clientes) }} clientes ·
                  {{ formatNum(qualityData.ativos || 0) }} ativos ·
                  {{ formatNum(qualityData.com_pedido_90d || 0) }} compraram (90d)
                </div>
                <div v-if="qualityData.dormentes > 0" class="text-warning small d-inline-flex align-items-center" style="gap: 0.2rem;">
                  <feather-icon icon="MoonIcon" size="11" />
                  {{ formatNum(qualityData.dormentes) }} dormentes
                </div>
              </div>
            </div>
            <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="closeQualityPanel">
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </div>

          <div class="py-1">
            <div
              v-for="dim in qualityData.dimensions"
              :key="dim.key"
              class="d-flex align-items-center mb-50"
              style="gap: 0.5rem;"
            >
              <div class="d-flex align-items-center text-muted small" style="gap: 0.3rem; min-width: 160px;">
                <feather-icon :icon="dim.icon" size="12" />
                <span>{{ dim.label }}</span>
              </div>
              <b-progress :max="100" class="flex-grow-1" height="6px">
                <b-progress-bar :value="dim.percent" :variant="getBarVariant(dim.percent)" />
              </b-progress>
              <span class="small font-weight-bold" :class="'text-' + getBarVariant(dim.percent)" style="min-width: 32px; text-align: right;">
                {{ dim.percent }}%
              </span>
            </div>
          </div>

          <div v-if="qualityData.insights && qualityData.insights.length" class="pt-1 border-top">
            <div
              v-for="(insight, idx) in qualityData.insights"
              :key="idx"
              class="d-flex align-items-start small p-50 mb-25 rounded"
              :class="'bg-light-' + insight.type + ' text-' + insight.type"
              style="gap: 0.4rem;"
            >
              <feather-icon :icon="insight.icon" size="12" />
              <span>{{ insight.text }}</span>
            </div>
          </div>
        </div>
      </b-card>
    </transition>

    <div v-if="showQualityPanel" class="quality-overlay" @click="closeQualityPanel"></div>

    <b-overlay :show="loading" variant="white" spinner-variant="primary" blur="0" opacity=".75" rounded="md">
      <b-card no-body class="mb-0">
        <div class="m-2">
          <b-row>
            <b-col
              cols="12"
              md="4"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="mb-0 mr-50">Mostrar</label>
              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                size="sm"
                class="per-page-selector d-inline-block mx-50"
                @change="onFilterChange"
              />
              <label class="mb-0">entradas</label>

              <b-button
                v-b-toggle.crm-clientes-filters
                size="sm"
                variant="outline-primary"
                class="ml-2"
              >
                <feather-icon icon="FilterIcon" />
                <span class="ml-25">Filtros</span>
                <b-badge v-if="activeFiltersCount" variant="light-primary" pill class="ml-25">
                  {{ activeFiltersCount }}
                </b-badge>
              </b-button>
            </b-col>

            <b-col cols="12" md="8">
              <div class="d-flex align-items-center justify-content-end flex-wrap" style="gap: 6px;">
                <b-input-group class="flex-grow-1" style="min-width: 240px; max-width: 420px;">
                  <b-form-input
                    v-model="filters.search"
                    placeholder="Razão social, fantasia ou CNPJ..."
                    debounce="400"
                    @update="onFilterChange"
                  />
                  <b-input-group-append>
                    <b-button :disabled="loading" variant="outline-danger" @click="clearFilters">
                      <feather-icon icon="XIcon" />
                    </b-button>
                    <b-button :disabled="loading" variant="outline-primary" @click="loadClientes">
                      <feather-icon icon="SearchIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>
            </b-col>
          </b-row>

          <b-collapse id="crm-clientes-filters" class="mt-1">
            <b-card border-variant="primary" class="mb-0">
              <b-row>
                <b-col cols="12" md="6" class="mb-1 mb-md-0">
                  <label class="small">Tipo CRM</label>
                  <b-form-select v-model="filters.crm_tipo" size="sm" @change="onFilterChange">
                    <b-form-select-option :value="null">Todos</b-form-select-option>
                    <b-form-select-option value="CLIENTE">Cliente</b-form-select-option>
                    <b-form-select-option value="LEAD">Lead</b-form-select-option>
                    <b-form-select-option value="PROSPECT">Prospect</b-form-select-option>
                    <b-form-select-option value="DESQUALIFICADO">Desqualificado</b-form-select-option>
                  </b-form-select>
                </b-col>
                <b-col cols="12" md="6">
                  <label class="small">Qualidade</label>
                  <b-form-select v-model="filters.quality" size="sm" @change="onFilterChange">
                    <b-form-select-option :value="null">Todos</b-form-select-option>
                    <b-form-select-option value="sem_email">Sem E-mail</b-form-select-option>
                    <b-form-select-option value="sem_telefone">Sem Telefone</b-form-select-option>
                    <b-form-select-option value="sem_endereco">Sem Endereço</b-form-select-option>
                  </b-form-select>
                </b-col>
              </b-row>
              <div class="d-flex justify-content-end mt-1">
                <b-button size="sm" variant="outline-secondary" @click="clearFilters">
                  <feather-icon icon="XIcon" />
                  <span class="ml-25">Limpar</span>
                </b-button>
              </div>
            </b-card>
          </b-collapse>
        </div>

        <b-table
          ref="refCrmClientesTable"
          class="position-relative"
          :items="clientes"
          :fields="fields"
          responsive
          hover
          show-empty
          primary-key="clienteid"
          empty-text="Nenhum cliente encontrado"
          tbody-tr-class="cursor-pointer"
          @row-clicked="onRowClick"
        >
          <template #cell(clienterazaosocial)="{ item }">
            <strong>{{ item.clienterazaosocial }}</strong>
          </template>

          <template #cell(clientecnpj)="{ item }">
            <span>{{ formatCnpj(item.clientecnpj) }}</span>
          </template>

          <template #cell(crm_tipo)="{ item }">
            <b-badge :variant="getTipoBadgeVariant(item.crm_tipo)" pill>
              {{ item.crm_tipo || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(representantenome)="{ item }">
            <span>{{ item.representantenome || '-' }}</span>
          </template>

          <template #cell(atividades_count)="{ item }">
            <b-badge variant="light-secondary" pill>{{ item.atividades_count || 0 }}</b-badge>
          </template>

          <template #cell(oportunidades_count)="{ item }">
            <b-badge variant="light-secondary" pill>{{ item.oportunidades_count || 0 }}</b-badge>
          </template>

          <template #cell(actions)="{ item }">
            <b-button
              variant="gradient-primary"
              size="sm"
              class="btn-icon"
              title="Visualizar"
              @click.stop="onRowClick(item)"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">
                Mostrando {{ totalRows ? ((currentPage - 1) * perPage) + 1 : 0 }}
                até {{ Math.min(currentPage * perPage, totalRows) }}
                de {{ totalRows }} entradas
              </span>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
                @change="onPageChange"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import crmClient from '@/api/CRM/crm.client';
import {
  BOverlay, BCard, BTable, BPagination, BFormInput, BFormSelect,
  BFormSelectOption, BButton, BBadge, BSpinner, BRow, BCol,
  BInputGroup, BInputGroupAppend, BCollapse, BProgress, BProgressBar,
  VBToggle,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CrmClientesList',
  components: {
    BOverlay,
    BCard,
    BTable,
    BPagination,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BButton,
    BBadge,
    BSpinner,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupAppend,
    BCollapse,
    BProgress,
    BProgressBar,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  data() {
    return {
      loading: false,
      clientes: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      perPageOptions: [10, 20, 50, 100],
      qualityData: null,
      qualityLoading: false,
      showQualityPanel: false,
      hoverTimer: null,
      animatedScore: 0,
      circumference: 2 * Math.PI * 34,
      filters: {
        search: '',
        crm_tipo: null,
        quality: null,
      },
      fields: [
        { key: 'clienterazaosocial', label: 'Razão Social', sortable: true },
        { key: 'clientenomefantasia', label: 'Fantasia', sortable: true },
        { key: 'clientecnpj', label: 'CNPJ' },
        { key: 'crm_tipo', label: 'Tipo CRM', class: 'text-center' },
        { key: 'representantenome', label: 'Representante' },
        { key: 'atividades_count', label: 'Atividades', class: 'text-center' },
        { key: 'oportunidades_count', label: 'Oportunidades', class: 'text-center' },
        { key: 'actions', label: 'Ações', class: 'text-center' },
      ],
    };
  },
  computed: {
    gradeClass() {
      if (!this.qualityData || !this.qualityData.grade) return 'grade-e';
      const g = this.qualityData.grade;
      if (g === 'A+') return 'grade-aplus';
      if (g === 'A') return 'grade-a';
      if (g === 'B') return 'grade-b';
      if (g === 'C') return 'grade-c';
      if (g === 'D') return 'grade-d';
      return 'grade-e';
    },
    gradeVariant() {
      if (!this.qualityData || !this.qualityData.grade) return 'secondary';
      const g = this.qualityData.grade;
      if (g === 'A+' || g === 'A') return 'success';
      if (g === 'B') return 'info';
      if (g === 'C') return 'warning';
      if (g === 'D') return 'danger';
      return 'secondary';
    },
    scoreOffset() {
      if (!this.qualityData) return this.circumference;
      const progress = this.qualityData.score / 100;
      return this.circumference * (1 - progress);
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filters.search) count++;
      if (this.filters.crm_tipo) count++;
      if (this.filters.quality) count++;
      return count;
    },
  },
  created() {
    this.loadClientes();
    this.loadQuality();
  },
  beforeDestroy() {
    this.cancelHoverTimer();
  },
  methods: {
    async loadQuality() {
      this.qualityLoading = true;
      try {
        const result = await crmClient.getPortfolioQuality();
        this.qualityData = result.data || result;
      } catch (error) {
        console.error('Erro ao carregar qualidade:', error);
      } finally {
        this.qualityLoading = false;
      }
    },
    animateScore(target) {
      this.animatedScore = 0;
      const duration = 800;
      const start = performance.now();
      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.animatedScore = Math.round(eased * target);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
    toggleQualityPanel() {
      this.cancelHoverTimer();
      this.showQualityPanel = !this.showQualityPanel;
      if (this.showQualityPanel) {
        this.animateScore(this.qualityData.score || 0);
      }
    },
    startHoverTimer() {
      if (this.showQualityPanel) return;
      this.hoverTimer = setTimeout(() => {
        this.showQualityPanel = true;
        this.animateScore(this.qualityData.score || 0);
      }, 2000);
    },
    cancelHoverTimer() {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
    },
    closeQualityPanel() {
      this.showQualityPanel = false;
      this.cancelHoverTimer();
    },
    getBarClass(percent) {
      if (percent >= 75) return 'bar-success';
      if (percent >= 50) return 'bar-warning';
      return 'bar-danger';
    },
    getBarVariant(percent) {
      if (percent >= 75) return 'success';
      if (percent >= 50) return 'warning';
      return 'danger';
    },
    formatNum(n) {
      if (!n && n !== 0) return '0';
      return Number(n).toLocaleString('pt-BR');
    },
    async loadClientes() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage,
        };
        if (this.filters.search) params.query = this.filters.search;
        if (this.filters.crm_tipo) params.tipo = this.filters.crm_tipo;
        if (this.filters.quality) params[this.filters.quality] = '1';

        const result = await crmClient.listClientes(params);
        const payload = result.data || result;
        this.clientes = payload.items || payload.rows || [];
        this.totalRows = payload.total || payload.count || this.clientes.length;
      } catch (error) {
        console.error('Erro ao carregar clientes CRM:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar clientes',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onFilterChange() {
      this.currentPage = 1;
      this.loadClientes();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadClientes();
    },
    clearFilters() {
      this.filters = { search: '', crm_tipo: null, quality: null };
      this.currentPage = 1;
      this.loadClientes();
    },
    onRowClick(item) {
      this.$router.push({ name: 'crm-cliente-detail', params: { id: item.clienteid } });
    },
    getTipoBadgeVariant(tipo) {
      const map = {
        CLIENTE: 'light-primary',
        LEAD: 'light-warning',
        PROSPECT: 'light-info',
        DESQUALIFICADO: 'light-secondary',
      };
      return map[tipo] || 'light-secondary';
    },
    formatCnpj(cnpj) {
      if (!cnpj) return '-';
      const c = cnpj.replace(/\D/g, '');
      if (c.length === 14) {
        return c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      }
      return cnpj;
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.cursor-pointer {
  cursor: pointer;
}
.quality-panel {
  position: absolute;
  top: 70px;
  right: 1rem;
  width: 520px;
  max-width: calc(100vw - 2rem);
  z-index: 100;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}
.quality-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
}
.quality-slide-enter-active,
.quality-slide-leave-active {
  transition: all 0.25s ease;
}
.quality-slide-enter,
.quality-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
